import React from 'react';
import PropTypes from 'prop-types';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import WizardStepDeleteButton
  from 'Views/Page/Content/Templates/WizardTemplates/WizardStepTemplate/Content/WizardStepDeleteButton';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import {
  FDAlert, FDDialogContent, FDDialogTitle, FDPrimaryActionButton, FDStack,
} from '@facet-wealth/design';
import { dataAttrs } from 'Helpers/Testing';
import MetaLayoutRemainingFields from 'Components/UI/Meta/MetaForm/Components/MetaLayout/MetaLayoutRemainingFields';

const FacetPlanAddExpense = ({ onSubmit }) => {
  const { saving, resource } = useMetaLayoutContext();
  const { clearModalCollection } = useModalContext();
  const { displayErrorMessage, displaySuccess } = useSnackbarContext();
  const { refreshPageContentCollection } = usePageContentContext();
  const [loading, setLoading] = React.useState(false);

  const isEditing = Boolean(resource.id() !== undefined);
  const canSubmit = resource.meta().canSubmit();
  const canDelete = resource.meta().canDelete();

  const handleOnSubmit = async () => {
    setLoading(true);
    try {
      const res = await resource.submit();

      displaySuccess(`Successfully ${isEditing ? 'Modified' : 'Added'} Expense`);

      // Refresh
      refreshPageContentCollection();
      onSubmit(res);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (isEditing && canDelete) {
        await resource.delete();

        // Close modal / display Success!
        clearModalCollection();
        displaySuccess('Successfully Removed Expense!');
      }
    } catch (err) {
      displayErrorMessage(err, err.errors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FDStack direction={'column'}>
      <FDDialogTitle value={`${isEditing ? 'Modify' : 'Add'} Expense`} />
      <FDDialogContent>
        <FDStack direction={'column'} spacing={2}>
          <FDAlert
            status={'warning'}
            title={'Mortgage and debt expenses should be added as liability accounts'}
          />
          <MetaControlField attr="name" />
          <MetaControlField attr="value" />
          <MetaControlField attr="valueConfidenceLevel" />
          <MetaControlField attr="frequency" />
          <MetaControlField attr="frequencyConfidenceLevel" />
          <MetaControlField attr="isEssential" />
          <MetaControlField attr="affectedByInflation" />
          <MetaControlField attr="expenseGroupName" />
          <MetaControlField attr="expenseCategoryName" />
          <MetaLayoutRemainingFields noAccordion />
          <MetaControlField attr="startType" />
          <MetaControlField attr="startDate" />
          <MetaControlField attr="startAtAge" />
          <MetaControlField attr="endType" />
          <MetaControlField attr="endDate" />
          <MetaControlField attr="endAtAge" />
          {isEditing && canDelete && (
            <WizardStepDeleteButton
              onDelete={handleDelete}
              onDeleteSuccess={() => refreshPageContentCollection()}
              dialogProps={{
                title: 'Are you sure?',
                message: 'Removing this item will delete all of its information. This cannot be undone.',
                cancelLabel: 'Nevermind',
                confirmLabel: 'Remove',
              }}
            />
          )}
          {canSubmit && (
            <FDPrimaryActionButton
              onClick={handleOnSubmit}
              label="Submit"
              loading={saving || loading}
              {...dataAttrs('facet-plan-add-expense-button', resource)}
            />
          )}
        </FDStack>
      </FDDialogContent>
    </FDStack>
  );
};

FacetPlanAddExpense.defaultProps = {
  onSubmit: () => {},
};

FacetPlanAddExpense.propTypes = {
  onSubmit: PropTypes.func,
};

export default FacetPlanAddExpense;
